import { useParams, Link } from "react-router-dom";
import { articlesBySlug, articles } from "@/mocks/articles";
import { categoryMap } from "@/mocks/categories";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articlesBySlug[slug] : undefined;

  if (!article) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-article-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-foreground-600 mb-6">该文章可能已被删除或不存在</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const category = categoryMap[article.category];
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: { "@type": "Person", name: article.author },
    datePublished: article.publishedAt,
    url: `https://51cgheibao.com/article/${article.slug}`,
    inLanguage: "zh-CN",
    articleSection: category?.name || "",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://51cgheibao.com/" },
      { "@type": "ListItem", position: 2, name: category?.name || "", item: `https://51cgheibao.com/category/${article.category}` },
      { "@type": "ListItem", position: 3, name: article.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <main className="pt-20">
        <article>
          <header className="py-8 md:py-12 px-4 md:px-8 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: category?.name || "", href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />

              <div className="mt-6">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="px-2.5 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full">
                    {category?.name}
                  </span>
                  {article.isHot && (
                    <span className="px-2.5 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                      热门
                    </span>
                  )}
                </div>

                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4 font-heading leading-tight">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.publishedAt}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {article.views.toLocaleString()} 次阅读
                  </span>
                </div>
              </div>
            </div>
          </header>

          <div className="py-10 px-4 md:px-8 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="flex-1 min-w-0">
                  <div className="mb-8 rounded-xl overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-auto object-cover"
                    />
                  </div>

                  <div
                    className="prose-custom text-foreground-800 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  <div className="mt-10 pt-8 border-t border-background-200">
                    <div className="flex flex-wrap gap-2">
                      <span className="text-sm text-foreground-500">标签：</span>
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-md"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mt-8 flex items-center gap-4">
                    <span className="text-sm text-foreground-500">分享到：</span>
                    <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="微博">
                      <i className="ri-weibo-line"></i>
                    </a>
                    <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="微信">
                      <i className="ri-wechat-line"></i>
                    </a>
                    <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                      <i className="ri-link"></i>
                    </a>
                  </div>
                </div>

                <aside className="lg:w-72 flex-shrink-0">
                  <div className="lg:sticky lg:top-24">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-4 uppercase tracking-wide">
                      相关文章
                    </h3>
                    <div className="space-y-4">
                      {relatedArticles.map((related) => (
                        <Link
                          key={related.id}
                          to={`/article/${related.slug}`}
                          className="group flex gap-3 cursor-pointer"
                        >
                          <div className="w-20 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                            <img
                              src={related.imageUrl}
                              alt={related.title}
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                              {related.title}
                            </h4>
                            <p className="text-xs text-foreground-500 mt-1">
                              {related.publishedAt}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>

                    <div className="mt-8 p-5 rounded-xl bg-background-100">
                      <h3 className="text-sm font-semibold text-foreground-950 mb-3">
                        温馨提示
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed">
                        本站内容仅供参考，不构成任何投资建议。投资有风险，入市需谨慎。如发现平台存在违法违规行为，请及时向相关部门举报。
                      </p>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </article>
      </main>
    </>
  );
}